import React from 'react';
import { Grid, Card, CardContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { buildBalanceCardEntries, balanceCardGridXs } from '../../utils/checkBalanceDisplay';

export function BalanceSummaryCards({
  cards,
  isMobile,
  compact = false,
  spacing,
  mb,
  children,
  totalCardCount,
}) {
  const cardPadding = compact
    ? { p: 1.5, '&:last-child': { pb: 1.5 } }
    : { p: isMobile ? 1 : 2, '&:last-child': { pb: isMobile ? 1 : 2 } };

  const valueVariant = compact
    ? (isMobile ? 'body1' : 'h5')
    : (isMobile ? 'h6' : 'h5');

  const valueWeight = compact ? 'bold' : undefined;
  const xs = balanceCardGridXs(totalCardCount ?? cards.length);

  return (
    <Grid container spacing={spacing ?? (compact ? 1.5 : 2)} sx={{ mb: mb ?? (compact ? 2 : 3) }}>
      {cards.map((card) => (
        <Grid item xs={6} sm={xs} md={xs} key={card.key}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent sx={cardPadding}>
              <Typography variant="caption" color="text.secondary" display={compact ? 'block' : undefined}>
                {card.label}
              </Typography>
              {card.valueSecondary && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ fontSize: '0.7rem', lineHeight: 1.2, mb: 0.25 }}
                >
                  {card.valueSecondary}
                </Typography>
              )}
              <Typography variant={valueVariant} color={card.color} fontWeight={card.bold ? 'bold' : valueWeight}>
                {card.value}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      ))}
      {children}
    </Grid>
  );
}

/**
 * @param {object} props
 * @param {number} props.totalItems
 * @param {number} props.totalDeposits
 * @param {number} props.amountDue
 * @param {number} [props.subtotal]
 * @param {number} [props.vatAmount]
 * @param {number} [props.vatPercentage]
 * @param {boolean} [props.pricesIncludeVat]
 * @param {string} [props.checkCurrency]
 * @param {string} [props.defaultItemCurrency]
 * @param {boolean} [props.showDualCurrency]
 * @param {number} [props.subtotalInDefaultCurrency]
 * @param {number} [props.vatAmountInDefaultCurrency]
 * @param {number} [props.totalInDefaultCurrency]
 * @param {number} [props.totalDepositsInDefaultCurrency]
 * @param {number} [props.amountDueInDefaultCurrency]
 * @param {boolean} props.isMobile
 * @param {boolean} [props.compact=false]
 * @param {number} [props.mb]
 * @param {React.ReactNode} [props.children] – extra grid items (e.g. exchange rate cards)
 * @param {number} [props.totalCardCount] – total grid slots including children
 * @param {number} [props.extraCardCount] – number of child grid cards (e.g. exchange rates)
 */
function CheckBalanceCards({
  totalItems,
  totalDeposits,
  amountDue,
  subtotal,
  vatAmount,
  vatPercentage,
  pricesIncludeVat,
  checkCurrency,
  defaultItemCurrency,
  showDualCurrency,
  subtotalInDefaultCurrency,
  vatAmountInDefaultCurrency,
  totalInDefaultCurrency,
  totalDepositsInDefaultCurrency,
  amountDueInDefaultCurrency,
  isMobile,
  compact = false,
  mb,
  children,
  totalCardCount,
  extraCardCount = 0,
}) {
  const { t } = useTranslation();
  const cards = buildBalanceCardEntries({
    totalItems,
    totalDeposits,
    amountDue,
    subtotal,
    vatAmount,
    vatPercentage,
    pricesIncludeVat,
    checkCurrency,
    defaultItemCurrency,
    showDualCurrency,
    subtotalInDefaultCurrency,
    vatAmountInDefaultCurrency,
    totalInDefaultCurrency,
    totalDepositsInDefaultCurrency,
    amountDueInDefaultCurrency,
    compact,
  }, t);

  const gridTotal = totalCardCount ?? cards.length + extraCardCount;
  const gridXs = balanceCardGridXs(gridTotal);
  const childWithGrid = React.isValidElement(children)
    ? React.cloneElement(children, { gridXs })
    : children;

  return (
    <BalanceSummaryCards
      cards={cards}
      isMobile={isMobile}
      compact={compact}
      mb={mb}
      totalCardCount={gridTotal}
    >
      {childWithGrid}
    </BalanceSummaryCards>
  );
}

export default CheckBalanceCards;
